import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import {
    acceptCashboxInvitation,
    acceptCashboxRequest,
    denyCashboxInvitation,
    denyCashboxRequest,
    getCashboxInviteList,
} from 'services/cashbox'

/**
 * «درخواست‌های عضویت» — the join side of the cashbox funnel.
 *
 * `invited-and-request-list` returns two different things in one flat list:
 *
 *   - cashboxes *I* have been invited to  (membership.is_member_accept false)
 *   - people asking to join a box *I own* (membership.is_owner_accept false)
 *
 * and the `membership` object it embeds is the one belonging to the row's
 * subject, not to the caller — so for a request row it describes the person
 * asking, not me. Those two flags are the only discriminator, and they decide
 * which endpoint the buttons hit: an invitation is accepted for the caller
 * (`accept-invitation/`, no id), a request is accepted for someone else
 * (`accept-request/<membership_id>/`).
 */
export default function CashboxInvitationsPage() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [pending, setPending] = useState<number | null>(null)

    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-invitations'],
        getCashboxInviteList
    )

    const { invitations, requests } = useMemo(() => {
        const rows = data?.results ?? []
        return {
            invitations: rows.filter(
                (row) => row.membership?.is_member_accept === false
            ),
            requests: rows.filter(
                (row) => row.membership?.is_owner_accept === false
            ),
        }
    }, [data])

    const settle = useMutation({
        mutationFn: async (action: {
            kind: 'invitation' | 'request'
            accept: boolean
            cashboxId: number
            membershipId?: number
        }) => {
            if (action.kind === 'invitation') {
                return action.accept
                    ? acceptCashboxInvitation(action.cashboxId)
                    : denyCashboxInvitation(action.cashboxId)
            }
            const payload = {
                id: action.cashboxId,
                membershipId: action.membershipId!,
            }
            return action.accept
                ? acceptCashboxRequest(payload)
                : denyCashboxRequest(payload)
        },
        onSuccess: (_result, action) => {
            // `kind` matters as much as the answer: an invitation is the member
            // joining a box someone made for them, a request is the owner
            // letting somebody in. Both land here, and only this tells them
            // apart.
            posthog.capture('cashbox_invitation_settled', {
                cashbox_id: action.cashboxId,
                action: action.accept ? 'accept' : 'deny',
                kind: action.kind,
            })
            toast.success(action.accept ? 'انجام شد' : 'رد شد')
            queryClient.invalidateQueries(['cashbox-invitations'])
            queryClient.invalidateQueries(['cashbox-messages'])
            // The boxes list is what the funds page renders; a newly accepted
            // membership only shows up there after it is refetched.
            queryClient.invalidateQueries(['get-boxes'])
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message
            toast.error(
                typeof message === 'string' && message
                    ? message
                    : 'مشکلی پیش آمده!'
            )
        },
        onSettled: () => setPending(null),
    })

    if (isLoading) return <DotsLoading />

    if (isError)
        return (
            <div className="bg-white min-h-screen">
                <Header title="درخواست‌های عضویت" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    const isEmpty = invitations.length === 0 && requests.length === 0

    return (
        <div className="bg-white min-h-screen">
            <Header title="درخواست‌های عضویت" />

            <div className="px-4 py-3">
                {isEmpty && (
                    <p className="text-sm text-text-secondary text-center py-10">
                        درخواستی وجود ندارد
                    </p>
                )}

                {invitations.length > 0 && (
                    <>
                        <p className="text-sm font-bold text-black mb-2">
                            دعوت به صندوق
                        </p>
                        {invitations.map((row) => (
                            <Card
                                key={`inv-${row.id}`}
                                title={`صندوق ${row.name}`}
                                subtitle={`مدیر: ${
                                    row.owner_name?.trim() || '—'
                                }`}
                                detail={`ارزش هر سهم ${toman(
                                    row.period?.share_value ?? 0
                                )} · ${faPlain.format(
                                    row.membership?.number_of_shares ?? 0
                                )} سهم برای شما`}
                                busy={pending === row.id}
                                onAccept={() => {
                                    setPending(row.id)
                                    settle.mutate({
                                        kind: 'invitation',
                                        accept: true,
                                        cashboxId: row.id,
                                    })
                                }}
                                onDeny={() => {
                                    setPending(row.id)
                                    settle.mutate({
                                        kind: 'invitation',
                                        accept: false,
                                        cashboxId: row.id,
                                    })
                                }}
                            />
                        ))}
                    </>
                )}

                {requests.length > 0 && (
                    <>
                        <p className="text-sm font-bold text-black mb-2 mt-4">
                            درخواست عضویت در صندوق شما
                        </p>
                        {requests.map((row) => (
                            <Card
                                key={`req-${row.id}-${row.membership?.id}`}
                                title={
                                    row.membership?.name?.trim() ||
                                    row.membership?.phone_number ||
                                    'متقاضی بدون نام'
                                }
                                subtitle={`صندوق ${row.name}`}
                                detail={`${faPlain.format(
                                    row.membership?.number_of_shares ?? 0
                                )} سهم درخواست شده`}
                                busy={pending === row.membership?.id}
                                onAccept={() => {
                                    setPending(row.membership!.id)
                                    settle.mutate({
                                        kind: 'request',
                                        accept: true,
                                        cashboxId: row.id,
                                        membershipId: row.membership!.id,
                                    })
                                }}
                                onDeny={() => {
                                    setPending(row.membership!.id)
                                    settle.mutate({
                                        kind: 'request',
                                        accept: false,
                                        cashboxId: row.id,
                                        membershipId: row.membership!.id,
                                    })
                                }}
                                onOpen={() => navigate(`/cashbox/${row.id}`)}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    )
}

function Card({
    title,
    subtitle,
    detail,
    busy,
    onAccept,
    onDeny,
    onOpen,
}: {
    title: string
    subtitle: string
    detail: string
    busy: boolean
    onAccept: () => void
    onDeny: () => void
    onOpen?: () => void
}) {
    return (
        <div className="rounded-card border border-divider bg-white p-3 mb-3">
            <button
                type="button"
                onClick={onOpen}
                disabled={!onOpen}
                className="block w-full text-right"
            >
                <p className="text-sm font-bold text-black truncate">{title}</p>
                <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>
                <p className="text-xs text-text-secondary mt-0.5">{detail}</p>
            </button>
            <div className="flex gap-2 mt-3">
                <button
                    type="button"
                    disabled={busy}
                    onClick={onAccept}
                    className="flex-1 h-10 rounded-[10px] bg-green-1 text-white text-sm font-bold disabled:opacity-50"
                >
                    تایید
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={onDeny}
                    className="flex-1 h-10 rounded-[10px] border border-divider text-text-secondary text-sm disabled:opacity-50"
                >
                    رد
                </button>
            </div>
        </div>
    )
}
